/**
 * Contexto de Tema
 *
 * Gerencia o tema claro/escuro da aplicação VendeAi.
 *
 * Funcionalidades:
 * - Alternância entre temas claro e escuro
 * - Persistência da preferência no localStorage
 * - NÃO detecta preferência do sistema (evita dark mode automático indesejado)
 * - Atualiza color-scheme do HTML para prevenir dark mode forçado pelo navegador
 * - Transição suave entre temas
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';

/**
 * Tipos de tema suportados
 */
type Theme = 'light' | 'dark';

/**
 * Interface do contexto de tema
 */
interface ThemeContextData {
  /** Tema atual */
  theme: Theme;
  /** Função para alternar entre temas */
  toggleTheme: () => void;
  /** Função para definir tema específico */
  setTheme: (theme: Theme) => void;
  /** Se o tema escuro está ativo */
  isDark: boolean;
  /**
   * Força o tema claro no documento enquanto a página pública estiver montada
   * (vitrines e website), sem alterar a preferência salva do usuário.
   * @returns função que libera o bloqueio
   */
  forceLight: () => () => void;
}

/**
 * Contexto de tema
 */
const ThemeContext = createContext<ThemeContextData>({} as ThemeContextData);

/**
 * Chave do localStorage para persistência
 */
const THEME_STORAGE_KEY = 'vendeai_theme';

/**
 * Props do provider de tema
 */
interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Provider de Tema
 *
 * Envolve a aplicação e fornece funcionalidades de tema.
 * Padrão: light. Não detecta preferência do sistema operacional
 * para evitar dark mode automático indesejado em navegadores.
 *
 * @example
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  // Estado do tema - recupera do localStorage ou usa light como padrão
  // NÃO detecta prefers-color-scheme do sistema para evitar dark mode automático
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
      if (savedTheme && ['light', 'dark'].includes(savedTheme)) {
        return savedTheme;
      }
    }
    return 'light';
  });

  // Quantidade de páginas públicas montadas que exigem tema claro
  const [forcedLightCount, setForcedLightCount] = useState(0);
  const appliedTheme: Theme = forcedLightCount > 0 ? 'light' : theme;

  const forceLight = useCallback(() => {
    setForcedLightCount(count => count + 1);
    return () => setForcedLightCount(count => Math.max(0, count - 1));
  }, []);

  /**
   * Aplica o tema no documento HTML
   */
  useEffect(() => {
    const root = window.document.documentElement;
    const body = window.document.body;

    // Remove classes anteriores
    root.classList.remove('light', 'dark');
    body.classList.remove('light', 'dark');

    // Adiciona classe do tema efetivo
    root.classList.add(appliedTheme);
    body.classList.add(appliedTheme);

    // Atualiza color-scheme do HTML para prevenir dark mode forçado pelo navegador
    root.style.colorScheme = appliedTheme;

    // Atualiza meta tag de theme-color para PWA. Na loja online a meta está com a cor
    // da vitrine (data-store-app, ver @utils/storeAppHead): só guarda a cor do painel
    // para quando o cliente sair da loja, sem sobrescrever a da loja (#291)
    const metaThemeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (metaThemeColor) {
      const panelColor = appliedTheme === 'dark' ? '#111827' : '#1f2937';
      metaThemeColor.dataset.appThemeColor = panelColor;
      if (!metaThemeColor.dataset.storeApp) metaThemeColor.setAttribute('content', panelColor);
    }
  }, [appliedTheme]);

  /**
   * Define um tema específico
   */
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  }, []);

  /**
   * Alterna entre tema claro e escuro
   */
  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        setTheme,
        isDark: theme === 'dark',
        forceLight,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook para acessar o contexto de tema
 *
 * @example
 * const { theme, toggleTheme, isDark } = useTheme();
 *
 * @returns Dados e funções do contexto de tema
 */
export function useTheme(): ThemeContextData {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme deve ser usado dentro de um ThemeProvider');
  }

  return context;
}
